import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Loader from '../../common/components/Loader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Visibility from '../../common/components/Visibility.tsx';
import {
    Paper,
    Box,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
    Button,
    Modal,
    Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import EditStorage from './EditStorage.tsx';
import StorageService from './services/StorageService.ts';
import { StorageLocationType } from '../../application/types/storageLocation.ts';

const StorageView = () => {
    const { id } = useParams();
    const [storageInventory, setStorageInventory] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [inEditMode, setInEditMode] = useState(false);
    const navigate = useNavigate();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const storageService = new StorageService();
    const [storageData, setStorageData] = useState({
        id: '',
        digitalId: '',
        name: '',
        location: '',
        description: '',
        isSatelliteStorage: false,
        inventory: [],
        barCode: '',
        type: StorageLocationType.StorageCabinet,
    });

    const getStorageTypeName = (type: StorageLocationType) => {
        switch (type) {
            case StorageLocationType.StorageCabinet:
                return 'Läkemedelsskåp';
            case StorageLocationType.OperatingRoom:
                return 'Operationssal';
            case StorageLocationType.AmbulanceBag:
                return 'Akutväska';
            default:
                return 'Okänd typ';
        }
    };

    const fetchStorage = async () => {
        setIsLoading(true);
        try {
            const storageInventoryResponse =
                await storageService.getStorageInventory(id);

            setStorageInventory(storageInventoryResponse);
            setStorageData({
                id: storageInventoryResponse.data.storageLocation.id,
                digitalId:
                    storageInventoryResponse.data.storageLocation.digitalId,
                name: storageInventoryResponse.data.storageLocation.name,
                location:
                    storageInventoryResponse.data.storageLocation.location,
                description:
                    storageInventoryResponse.data.storageLocation.description,
                isSatelliteStorage:
                    storageInventoryResponse.data.storageLocation
                        .isSatelliteStorage,
                inventory: storageInventoryResponse.data.inventory,
                barCode: storageInventoryResponse.data.storageLocation.barCode,
                type: storageInventoryResponse.data.storageLocation.type,
            });
        } catch (error) {
            setError(
                'Error fetching storageManagement details. Please try again.'
            );
            console.error('Error fetching storageManagement details:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!inEditMode) {
            fetchStorage();
        }
    }, [id, inEditMode]);

    const deleteStorage = async () => {
        try {
            await storageService.deleteStorage(id);
            navigate('..');
        } catch (error) {
            console.error('Error deleting storage:', error);
            alert('Något gick fel.');
        }
    };

    const handleStorageUpdate = (data) => {
        setStorageData((prevState) => ({
            ...prevState,
            ...data,
        }));
    };

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <p className="text-red-500">{error}</p>;
    }

    if (!storageInventory) {
        return <p className="text-red-500">Storage not found.</p>;
    }

    return (
        <div>
            <Modal
                className="flex justify-center items-center"
                open={modalIsOpen}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Paper sx={{ width: 400, padding: '25px' }}>
                    <Typography
                        id="modal-modal-title"
                        variant="h6"
                        component="h2"
                    >
                        Vill du arkivera lagerstället?
                    </Typography>
                    <Button
                        onClick={() => {
                            setModalIsOpen(false);
                        }}
                    >
                        Avbryt
                    </Button>
                    <Button sx={{ color: 'red' }} onClick={deleteStorage}>
                        Arkivera
                    </Button>
                </Paper>
            </Modal>
            <Box
                sx={{
                    p: { xs: 2, sm: 3 },
                    maxWidth: '1400px',
                    margin: '0 auto',
                }}
            >
                <Visibility visible={inEditMode}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 2,
                        }}
                    >
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => setInEditMode(false)}
                            variant="outlined"
                            color="primary"
                        >
                            Tillbaka
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                                setModalIsOpen(true);
                            }}
                            startIcon={<FontAwesomeIcon icon={faTrash} />}
                        >
                            Arkivera lagerställe
                        </Button>
                    </Box>
                </Visibility>
                <Visibility visible={!inEditMode}>
                    <Box sx={{ mb: 2 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('..')}
                            variant="outlined"
                            color="primary"
                        >
                            Tillbaka
                        </Button>
                    </Box>
                </Visibility>
            </Box>
            <div className="p-4 sm:p-6 max-w-6xl mx-auto">
                <h1 className="text-3xl sm:text-4xl font-bold mb-6 sm:mb-8">
                    {storageData?.name}
                </h1>
                <Visibility visible={!inEditMode}>
                    {/* Information Card */}
                    <Paper
                        elevation={3}
                        className="p-6 sm:p-8 mb-6"
                        sx={{
                            borderRadius: 2,
                            backgroundColor: '#ffffff',
                        }}
                    >
                        <Typography
                            variant="h5"
                            fontWeight="bold"
                            gutterBottom
                            sx={{ mb: 3 }}
                        >
                            Lagerställeinformation
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: '1fr 1fr',
                                    md: 'repeat(4, 1fr)',
                                },
                                gap: 3,
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{ mb: 0.5, fontWeight: 600 }}
                                >
                                    Plats
                                </Typography>
                                <Typography variant="body1">
                                    {storageData?.location || 'Ej angiven'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{ mb: 0.5, fontWeight: 600 }}
                                >
                                    Beskrivning
                                </Typography>
                                <Typography variant="body1">
                                    {storageData?.description ||
                                        'Ingen beskrivning'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{ mb: 0.5, fontWeight: 600 }}
                                >
                                    Akutväska/Satellitlagring
                                </Typography>
                                <Typography variant="body1">
                                    {storageData?.isSatelliteStorage
                                        ? 'Ja'
                                        : 'Nej'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{ mb: 0.5, fontWeight: 600 }}
                                >
                                    Typ
                                </Typography>
                                <Typography variant="body1">
                                    {getStorageTypeName(storageData?.type)}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                    {/* Inventory Table */}
                    <Paper
                        elevation={3}
                        sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                p: { xs: 2, sm: 3 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 2,
                                backgroundColor: '#f5f5f5',
                                borderBottom: '1px solid #e0e0e0',
                            }}
                        >
                            <Typography variant="h5" fontWeight="bold">
                                Gränsvärden
                            </Typography>
                            <Button
                                onClick={() => setInEditMode(true)}
                                variant="contained"
                                startIcon={<EditNoteIcon />}
                                size="medium"
                            >
                                Uppdatera
                            </Button>
                        </Box>

                        <Box sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead>
                                    <TableRow
                                        sx={{ backgroundColor: '#fafafa' }}
                                    >
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            Läkemedel
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Maximalt lagersaldo
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Beställningspunkt
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Saldo
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Status
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {storageData.inventory.length > 0 ? (
                                        storageData.inventory.map(
                                            (inventory, index) => (
                                                <TableRow
                                                    key={`${inventory.drug.id}-${index}`}
                                                    sx={{
                                                        '&:nth-of-type(odd)': {
                                                            backgroundColor:
                                                                '#fafafa',
                                                        },
                                                        '&:hover': {
                                                            backgroundColor:
                                                                '#f0f0f0',
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {`${inventory.drug.drugName} ${inventory.drug.concentration} ${inventory.drug.concentrationUnit}, ${inventory.drug.quantity} ${inventory.drug.quantityUnit}`}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={500}
                                                        >
                                                            {inventory
                                                                .saldoThresholds
                                                                ?.maxValue ||
                                                                'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={500}
                                                        >
                                                            {inventory
                                                                .saldoThresholds
                                                                ?.restockThreshold ||
                                                                'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={600}
                                                        >
                                                            {String(
                                                                inventory.quantityRemaining
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box
                                                            sx={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius:
                                                                    '50%',
                                                                margin: '0 auto',
                                                                backgroundColor:
                                                                    inventory.quantityRemaining >=
                                                                    (inventory
                                                                        .saldoThresholds
                                                                        ?.maxValue -
                                                                        inventory
                                                                            .saldoThresholds
                                                                            ?.restockThreshold) /
                                                                        2 +
                                                                        inventory
                                                                            .saldoThresholds
                                                                            ?.restockThreshold
                                                                        ? '#4caf50'
                                                                        : inventory.quantityRemaining >
                                                                            inventory
                                                                                .saldoThresholds
                                                                                ?.restockThreshold
                                                                          ? '#ff9800'
                                                                          : '#f44336',
                                                                boxShadow: 1,
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={5}
                                                align="center"
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ py: 3 }}
                                                >
                                                    Inga läkemedel i lagret
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </Paper>
                </Visibility>
            </div>
            <Visibility visible={inEditMode}>
                <EditStorage
                    storageData={storageData}
                    setStorageData={handleStorageUpdate}
                />
            </Visibility>
        </div>
    );
};

export default StorageView;
